/**
 * RoadPositioningDiagram — 10 SVGs for the Road Positioning Hub.
 *
 * Sister component to JunctionDiagram. Same visual language. Each
 * diagram emphasises the positioning/reading cue rather than the
 * junction layout — yellow car for "you", grey for others, dashed
 * sightlines, arrows for vision direction.
 */

const ROAD = "#334155";
const PAINT = "#fde68a";
const ACCENT = "#10b981";
const CAR_YOU = "#facc15";   // yellow — you
const CAR_OTHER = "#94a3b8"; // slate — others
const SIGHTLINE = "#10b981";
const HAZARD = "#ef4444";

function Frame({ children, label }) {
  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
         className="w-full h-full">
      <rect width="200" height="200" fill="#0f172a" rx="12" />
      <defs>
        <pattern id="rp-grid" width="20" height="20"
                 patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none"
                stroke="#1e293b" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#rp-grid)" rx="12" />
      {children}
      {label && (
        <text x="100" y="190" textAnchor="middle"
              fill="#94a3b8" fontSize="9" fontFamily="ui-sans-serif">
          {label}
        </text>
      )}
    </svg>
  );
}

function Car({ x, y, w = 10, h = 16, color = CAR_OTHER, rotate = 0 }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h}
            fill={color} rx="2" stroke="#0f172a" strokeWidth="0.5" />
      <rect x={-w / 2 + 1.5} y={-h / 2 + 2} width={w - 3} height={3}
            fill="rgba(255,255,255,0.25)" />
    </g>
  );
}

// ═══════ Lane structure ═══════
function LaneStructure() {
  return (
    <Frame label="Lane structure">
      <rect x="40" y="0" width="120" height="200" fill={ROAD} />
      {/* Three lanes */}
      <line x1="80"  y1="0" x2="80"  y2="200" stroke={PAINT} strokeDasharray="8 4" strokeWidth="1.2" />
      <line x1="120" y1="0" x2="120" y2="200" stroke={PAINT} strokeDasharray="8 4" strokeWidth="1.2" />
      {/* Painted arrows */}
      <text x="60" y="40" fontSize="12" fill={PAINT} textAnchor="middle">↑</text>
      <text x="100" y="40" fontSize="12" fill={PAINT} textAnchor="middle">↑</text>
      <text x="140" y="40" fontSize="12" fill={PAINT} textAnchor="middle">↑</text>
      <Car x="60" y="150" color={CAR_YOU} />
      <Car x="100" y="170" />
      <Car x="140" y="160" />
    </Frame>
  );
}

// ═══════ Visibility ═══════
function Visibility() {
  return (
    <Frame label="Visibility">
      <rect x="60" y="0" width="80" height="200" fill={ROAD} />
      {/* Crest of hill — fade */}
      <rect x="60" y="0" width="80" height="80" fill="url(#fadeOut)" />
      <defs>
        <linearGradient id="fadeOut" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0f172a" stopOpacity="1" />
          <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="100" y1="80" x2="100" y2="200" stroke={PAINT} strokeDasharray="8 4" strokeWidth="1.2" />
      <Car x="100" y="170" color={CAR_YOU} />
      {/* Sightline arrows pointing up into fog */}
      <line x1="100" y1="160" x2="100" y2="100" stroke={SIGHTLINE} strokeWidth="0.8" strokeDasharray="2 3" />
      <text x="100" y="60" fontSize="10" fill={HAZARD} textAnchor="middle">?</text>
    </Frame>
  );
}

// ═══════ Bends ═══════
function Bends() {
  return (
    <Frame label="Bends">
      <path d="M 60 0 Q 60 100 140 140 L 140 200 L 180 200 L 180 140 Q 180 60 100 60 L 100 0 Z"
            fill={ROAD} />
      <path d="M 80 0 Q 80 80 140 110 L 140 160 L 160 160 L 160 110 Q 160 80 100 80 L 100 0"
            fill="none" stroke={PAINT} strokeDasharray="6 4" strokeWidth="1" />
      <Car x="100" y="170" color={CAR_YOU} rotate={-15} />
      {/* Vanishing-point marker */}
      <circle cx="180" cy="120" r="4" fill={ACCENT} opacity="0.7" />
      <line x1="100" y1="160" x2="178" y2="120" stroke={SIGHTLINE} strokeWidth="0.6" strokeDasharray="2 3" />
    </Frame>
  );
}

// ═══════ Junction approach ═══════
function JunctionApproach() {
  return (
    <Frame label="Junction approach">
      <rect x="60" y="80" width="80" height="120" fill={ROAD} />
      <rect x="0" y="80" width="200" height="40" fill={ROAD} />
      <line x1="100" y1="120" x2="100" y2="200" stroke={PAINT} strokeDasharray="8 4" strokeWidth="1.2" />
      {/* Painted arrows on tarmac well before the junction */}
      <text x="100" y="180" fontSize="14" fill={PAINT} textAnchor="middle">↰</text>
      <text x="100" y="155" fontSize="14" fill={PAINT} textAnchor="middle">↑</text>
      <Car x="100" y="165" color={CAR_YOU} />
      {/* Sightline up to the junction */}
      <line x1="100" y1="160" x2="100" y2="100" stroke={SIGHTLINE} strokeWidth="0.6" strokeDasharray="2 3" />
    </Frame>
  );
}

// ═══════ Parked cars ═══════
function ParkedCars() {
  return (
    <Frame label="Parked cars">
      <rect x="40" y="0" width="120" height="200" fill={ROAD} />
      <line x1="100" y1="0" x2="100" y2="200" stroke={PAINT} strokeDasharray="8 4" strokeWidth="1.2" />
      {/* Parked row on left */}
      <Car x="55" y="60"  />
      <Car x="55" y="100" />
      <Car x="55" y="140" />
      {/* You — moved out for clearance */}
      <Car x="80" y="180" color={CAR_YOU} />
      {/* Sightline curving past parked row */}
      <line x1="80" y1="170" x2="55" y2="40" stroke={SIGHTLINE} strokeWidth="0.6" strokeDasharray="2 3" />
    </Frame>
  );
}

// ═══════ Meeting traffic ═══════
function MeetingTraffic() {
  return (
    <Frame label="Meeting traffic">
      <rect x="50" y="0" width="100" height="200" fill={ROAD} />
      <line x1="100" y1="0" x2="100" y2="200" stroke={PAINT} strokeDasharray="6 4" strokeWidth="1.2" />
      {/* Parked car making a pinch on left */}
      <Car x="65" y="70" />
      <Car x="100" y="170" color={CAR_YOU} />
      {/* Oncoming */}
      <Car x="115" y="30" rotate={180} />
      <text x="100" y="105" fontSize="8" fill={HAZARD} textAnchor="middle">give way</text>
    </Frame>
  );
}

// ═══════ Dual carriageway ═══════
function DualCarriageway() {
  return (
    <Frame label="Dual carriageway">
      <rect x="20" y="0" width="80" height="200" fill={ROAD} />
      <rect x="110" y="0" width="80" height="200" fill={ROAD} />
      {/* Central reservation */}
      <rect x="100" y="0" width="10" height="200" fill="#1e293b" stroke={ACCENT} strokeWidth="0.5" />
      <line x1="60"  y1="0" x2="60"  y2="200" stroke={PAINT} strokeDasharray="8 4" />
      <line x1="150" y1="0" x2="150" y2="200" stroke={PAINT} strokeDasharray="8 4" />
      <Car x="40" y="170" color={CAR_YOU} />
      <Car x="80" y="80"  />
      <Car x="170" y="50"  rotate={180} />
      <Car x="130" y="120" rotate={180} />
    </Frame>
  );
}

// ═══════ Lane markings ═══════
function LaneMarkings() {
  return (
    <Frame label="Lane markings">
      <rect x="40" y="0" width="120" height="200" fill={ROAD} />
      {/* Short-dash = caution */}
      <line x1="100" y1="0" x2="100" y2="60" stroke={PAINT} strokeDasharray="3 3" strokeWidth="1.4" />
      {/* Long-dash = standard */}
      <line x1="100" y1="60" x2="100" y2="120" stroke={PAINT} strokeDasharray="12 6" strokeWidth="1.4" />
      {/* Solid = no-cross */}
      <line x1="100" y1="120" x2="100" y2="200" stroke={PAINT} strokeWidth="1.6" />
      <Car x="80" y="170" color={CAR_YOU} />
      <text x="160" y="40" fontSize="8" fill={ACCENT}>caution</text>
      <text x="160" y="95" fontSize="8" fill={ACCENT}>normal</text>
      <text x="160" y="160" fontSize="8" fill={HAZARD}>no-cross</text>
    </Frame>
  );
}

// ═══════ Road width ═══════
function RoadWidth() {
  return (
    <Frame label="Road width">
      {/* Narrow at bottom, wide at top */}
      <path d="M 60 0 L 140 0 L 170 200 L 30 200 Z" fill={ROAD} />
      <line x1="100" y1="0" x2="100" y2="200" stroke={PAINT} strokeDasharray="8 4" />
      <Car x="80" y="170" color={CAR_YOU} />
      <line x1="30" y1="195" x2="170" y2="195" stroke={ACCENT} strokeWidth="0.6" />
      <text x="100" y="50" fontSize="7" fill={ACCENT} textAnchor="middle">more margin</text>
      <text x="100" y="190" fontSize="7" fill={HAZARD} textAnchor="middle">less margin</text>
    </Frame>
  );
}

// ═══════ Road speed ═══════
function RoadSpeed() {
  return (
    <Frame label="Road speed">
      <rect x="60" y="0" width="80" height="200" fill={ROAD} />
      <line x1="100" y1="0" x2="100" y2="200" stroke={PAINT} strokeDasharray="8 4" />
      <Car x="100" y="170" color={CAR_YOU} />
      {/* 30 mph circular sign */}
      <circle cx="40" cy="50" r="14" fill="white" stroke={HAZARD} strokeWidth="3" />
      <text x="40" y="55" fontSize="11" fontWeight="700" fill="#0f172a" textAnchor="middle">30</text>
      {/* Planning horizon arrow */}
      <line x1="100" y1="155" x2="100" y2="40" stroke={SIGHTLINE} strokeWidth="0.6" strokeDasharray="2 3" />
      <text x="100" y="30" fontSize="7" fill={ACCENT} textAnchor="middle">plan ahead</text>
    </Frame>
  );
}

const DIAGRAMS = {
  lane_structure:    LaneStructure,
  visibility:        Visibility,
  bends:             Bends,
  junction_approach: JunctionApproach,
  parked_cars:       ParkedCars,
  meeting_traffic:   MeetingTraffic,
  dual_carriageway:  DualCarriageway,
  lane_markings:     LaneMarkings,
  road_width:        RoadWidth,
  road_speed:        RoadSpeed,
};

export default function RoadPositioningDiagram({ id, className = "" }) {
  const Cmp = DIAGRAMS[id] || LaneStructure;
  return (
    <div className={className} data-testid={`road-positioning-diagram-${id}`}>
      <Cmp />
    </div>
  );
}
